import { Badge } from '../common';
import './chat.css';

/**
 * Which image the next question is about — and, on each turn, which it was about.
 *
 * A thread holding three photographs has three separate codings in it, and the model
 * cannot tell them apart: images reach it unlabelled, so "why did you code the
 * sidewalk as absent?" has no referent unless the client says which one. The API takes
 * a `target_image_id` for exactly that, and until now nothing in the UI sent one — so
 * every follow-up silently inherited whichever image was discussed last, which is
 * right for "why that value?" one turn later and wrong for any question about a
 * specific picture.
 *
 * Hidden below two images, deliberately. In a single-image thread there is nothing to
 * choose and a chip row would be one more piece of furniture explaining a decision
 * that does not exist.
 */
export function ImageTargetBar({ images, value, onChange, resolved, disabled = false }) {
  if (!images || images.length < 2) return null;

  const inherited = images.find((image) => image.image_id === resolved?.image_id);

  return (
    <div className="targets" role="group" aria-label="Which image the next question is about">
      <span className="targets__label">Asking about</span>

      <button
        type="button"
        className={`chip chip--button ${value ? '' : 'chip--accent'}`}
        aria-pressed={!value}
        disabled={disabled}
        onClick={() => onChange(null)}
        title={
          inherited
            ? `Follow-ups continue with the image last discussed — currently image ${inherited.index}`
            : 'Follow-ups continue with the image last discussed'
        }
      >
        Auto{inherited ? ` · image ${inherited.index}` : ''}
      </button>

      {images.map((image) => (
        <button
          key={image.image_id}
          type="button"
          className={`chip chip--button ${value === image.image_id ? 'chip--accent' : ''}`}
          aria-pressed={value === image.image_id}
          disabled={disabled}
          onClick={() => onChange(image.image_id)}
          title={image.filename || image.image_id}
        >
          {image.index} · <span className="targets__name">{label(image)}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Which image a stored turn was about.
 *
 * Without this a transcript of nine follow-ups across three images is unreadable a
 * week later, even though the data to render it is on every row. The number matches
 * the chips above and the `index` the API returns, so what a person reads here is what
 * they can type into `target`.
 */
export function TargetStamp({ images, imageId }) {
  if (!imageId || !images || images.length < 2) return null;
  const entry = images.find((image) => image.image_id === imageId);
  if (!entry) return null;
  return <Badge title={entry.filename || entry.image_id}>image {entry.index}</Badge>;
}

/**
 * What the server did with the question, when it is worth saying out loud.
 *
 * Only for the cases a person needs to know about: a reference read out of their own
 * prose, and a reference that could not be honoured. An inherited target is already
 * shown on the Auto chip, and repeating it under every answer would be noise.
 */
export function TargetNotice({ resolved, images }) {
  if (!resolved) return null;
  const entry = images?.find((image) => image.image_id === resolved.image_id);

  if (resolved.note) {
    return (
      <span className="targets__note" style={{ color: 'var(--state-warn)' }}>
        {resolved.note}
        {entry ? ` Answered about image ${entry.index}.` : ''}
      </span>
    );
  }
  if (resolved.how === 'prompt_reference' && entry) {
    return (
      <span className="targets__note">
        Your question named image {entry.index}
        {entry.filename ? ` (${entry.filename})` : ''}, so it was answered about that one.
      </span>
    );
  }
  return null;
}

function label(image) {
  if (image.label) return image.label;
  if (image.filename) return image.filename;
  return image.image_id.slice(0, 8);
}
